import { toLangCode, type LangCode } from '@/lib/baba-api'
import {
  DEFAULT_SOURCE,
  DEFAULT_TARGET,
  DRAFT_KEY,
  PREFS_KEY,
  RECENT_MAX,
  RECENT_TRANSLATIONS_KEY,
} from './constants'
import type { AppPrefs, RecentTranslation, TranslatorTab } from './types'

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      const comma = result.indexOf(',')
      resolve(comma >= 0 ? result.slice(comma + 1) : result)
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

/**
 * Persisted prefs are only as trustworthy as whatever wrote them. Restoring a
 * history row used to store the ledger's raw language value ('English'), and
 * because prefs are saved on every language change that value outlived the
 * click: the Translate page then crashed on mount, every load, until storage
 * was cleared by hand. Sanitizing on the way OUT heals those browsers without
 * asking anyone to clear anything.
 */
export function loadPrefs(): Partial<AppPrefs> {
  if (typeof localStorage === 'undefined') return {}
  try {
    const raw = localStorage.getItem(PREFS_KEY)
    const prefs: Partial<AppPrefs> = raw ? JSON.parse(raw) : {}
    if (prefs.sourceLang) prefs.sourceLang = toLangCode(prefs.sourceLang, DEFAULT_SOURCE)
    if (prefs.targetLang) prefs.targetLang = toLangCode(prefs.targetLang, DEFAULT_TARGET)
    if (prefs.tab) prefs.tab = normalizeTab(prefs.tab)
    return prefs
  } catch {
    return {}
  }
}

/**
 * The separate Document ('pdf'), Image ('image') and older Word ('doc') tabs
 * are one File tab now. Anyone who last used one of them has that id sitting
 * in localStorage, and anyone who bookmarked a link to it still asks for it,
 * so every retired id lands on the surface that replaced it rather than on a
 * tab that no longer exists.
 */
export function normalizeTab(tab: string | undefined | null): TranslatorTab {
  return tab === 'text' ? 'text' : tab ? 'file' : 'text'
}

export function savePrefs(patch: Partial<AppPrefs>): void {
  if (typeof localStorage === 'undefined') return
  try {
    const existing = loadPrefs()
    localStorage.setItem(PREFS_KEY, JSON.stringify({ ...existing, ...patch }))
  } catch {}
}

export function detectScript(text: string): LangCode | null {
  const s = text.trim()
  if (!s) return null
  let hebrew = 0, arabic = 0, latin = 0, total = 0
  for (const ch of s) {
    const cp = ch.codePointAt(0) ?? 0
    if (cp >= 0x0590 && cp <= 0x05FF) { hebrew++; total++ }
    else if (cp >= 0x0600 && cp <= 0x06FF) { arabic++; total++ }
    else if ((cp >= 0x0041 && cp <= 0x007A) || (cp >= 0x00C0 && cp <= 0x024F)) { latin++; total++ }
  }
  if (total === 0) return null
  if (hebrew / total > 0.4) return 'he'
  if (arabic / total > 0.4) return 'ar'
  if (latin / total > 0.3) return 'en'
  return null
}

export function scaledFontSize(len: number): string {
  if (len < 80) return 'text-2xl'
  if (len < 200) return 'text-xl'
  if (len < 600) return 'text-lg'
  if (len < 1500) return 'text-base'
  return 'text-sm'
}

export function loadRecentTranslations(): RecentTranslation[] {
  try {
    const raw = localStorage.getItem(RECENT_TRANSLATIONS_KEY)
    return raw ? JSON.parse(raw) : []
  } catch { return [] }
}

export function pushRecentTranslation(item: RecentTranslation): RecentTranslation[] {
  const current = loadRecentTranslations().filter((t) => t.id !== item.id)
  const next = [item, ...current].slice(0, RECENT_MAX)
  try { localStorage.setItem(RECENT_TRANSLATIONS_KEY, JSON.stringify(next)) } catch {}
  return next
}

export function loadDraftText(): string {
  try { return localStorage.getItem(DRAFT_KEY) || '' } catch { return '' }
}

export function persistDraftText(text: string, tab: TranslatorTab): void {
  if (tab !== 'text') return
  try {
    if (text) localStorage.setItem(DRAFT_KEY, text)
    else localStorage.removeItem(DRAFT_KEY)
  } catch {}
}
